import { FrequencyPattern } from '@/types/challenge'
import { Button } from './Button'
import { useTranslation } from '@/i18n/hooks/useTranslation'

interface FrequencyBuilderProps {
  value: FrequencyPattern
  onChange: (frequency: FrequencyPattern) => void
}

const DAYS_OF_WEEK = [
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
  { value: 0, label: 'Sun' },
]

export function FrequencyBuilder({ value, onChange }: FrequencyBuilderProps) {
  const { t } = useTranslation()

  const handleModeChange = (newMode: FrequencyPattern['mode']) => {
    const basePattern: FrequencyPattern = {
      mode: newMode,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }

    switch (newMode) {
      case 'daily':
        onChange({
          ...basePattern,
          times: value.times && value.times.length > 0 ? value.times : ['09:00'],
        })
        break
      case 'hourly':
        onChange({
          ...basePattern,
          startHour: 9,
          endHour: 18,
        })
        break
      case 'weekly':
        onChange({
          ...basePattern,
          daysOfWeek: [1], // Monday
          times: ['09:00'],
        })
        break
      default:
        onChange(basePattern)
    }
  }

  const handleTimeChange = (index: number, time: string) => {
    const newTimes = [...(value.times || [])]
    newTimes[index] = time
    onChange({ ...value, times: newTimes })
  }

  const addTime = () => {
    const newTimes = [...(value.times || []), '12:00']
    onChange({ ...value, times: newTimes })
  }

  const removeTime = (index: number) => {
    const newTimes = value.times?.filter((_, i) => i !== index) || []
    onChange({ ...value, times: newTimes })
  }

  const toggleDay = (day: number) => {
    const currentDays = value.daysOfWeek || []
    const newDays = currentDays.includes(day)
      ? currentDays.filter(d => d !== day)
      : [...currentDays, day].sort()
    onChange({ ...value, daysOfWeek: newDays })
  }

  return (
    <div className="space-y-lg">
      <div>
        <div className="grid grid-cols-3 gap-sm">
          <button
            type="button"
            onClick={() => handleModeChange('daily')}
            className={`p-md border rounded-full text-sm transition-colors ${
              value.mode === 'daily'
                ? 'border-primary bg-primary text-white dark:text-black'
                : 'border-border bg-transparent text-text hover:bg-bg-alt'
            }`}
          >
            {t('challenges.daily')}
          </button>
          <button
            type="button"
            onClick={() => handleModeChange('hourly')}
            className={`p-md border rounded-full text-sm transition-colors ${
              value.mode === 'hourly'
                ? 'border-primary bg-primary text-white dark:text-black'
                : 'border-border bg-transparent text-text hover:bg-bg-alt'
            }`}
          >
            {t('challenges.hourly')}
          </button>
          <button
            type="button"
            onClick={() => handleModeChange('weekly')}
            className={`p-md border rounded-full text-sm transition-colors ${
              value.mode === 'weekly'
                ? 'border-primary bg-primary text-white dark:text-black'
                : 'border-border bg-transparent text-text hover:bg-bg-alt'
            }`}
          >
            {t('challenges.weekly')}
          </button>
        </div>
      </div>

      {value.mode === 'daily' && (
        <div>
          <label className="block text-sm font-medium text-text mb-sm">
            {t('challenges.times')}
          </label>
          <div className="space-y-sm">
            {value.times?.map((time, index) => (
              <div key={index} className="flex items-center gap-sm">
                <input
                  type="time"
                  value={time}
                  onChange={(e) => handleTimeChange(index, e.target.value)}
                  className="px-md py-sm border border-border rounded-full bg-bg text-text dark-time-input"
                />
                {(value.times?.length || 0) > 1 && (
                  <Button
                    type="button"
                    variant="tertiary"
                    size="sm"
                    onClick={() => removeTime(index)}
                  >
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    {t('common.remove')}
                  </Button>
                )}
              </div>
            ))}
            <Button type="button" variant="secondary" size="sm" onClick={addTime}>
              {t('challenges.addTime')}
            </Button>
          </div>
        </div>
      )}

      {value.mode === 'hourly' && (
        <div className="grid grid-cols-2 gap-lg">
          <div>
            <label className="block text-sm font-medium text-text mb-sm">
              {t('challenges.startHour')}
            </label>
            <select
              value={value.startHour || 9}
              onChange={(e) => onChange({ ...value, startHour: parseInt(e.target.value) })}
              className="w-full px-md py-sm border border-border rounded-full bg-bg text-text"
            >
              {Array.from({ length: 24 }, (_, i) => (
                <option key={i} value={i}>
                  {i.toString().padStart(2, '0')}:00
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-text mb-sm">
              {t('challenges.endHour')}
            </label>
            <select
              value={value.endHour || 18}
              onChange={(e) => onChange({ ...value, endHour: parseInt(e.target.value) })}
              className="w-full px-md py-sm border border-border rounded-full bg-bg text-text"
            >
              {Array.from({ length: 24 }, (_, i) => i + 1).map((i) => (
                <option key={i} value={i}>
                  {i.toString().padStart(2, '0')}:00
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {value.mode === 'weekly' && (
        <div>
          <label className="block text-sm font-medium text-text mb-sm">
            {t('challenges.daysOfWeek')}
          </label>
          <div className="flex gap-xs mb-lg">
            {DAYS_OF_WEEK.map((day) => (
              <button
                key={day.value}
                type="button"
                onClick={() => toggleDay(day.value)}
                className={`px-md py-sm rounded-pill text-sm transition-colors ${
                  value.daysOfWeek?.includes(day.value)
                    ? 'bg-primary text-white dark:text-black'
                    : 'bg-bg-alt text-text hover:bg-border'
                }`}
              >
                {day.label}
              </button>
            ))}
          </div>
          
          <label className="block text-sm font-medium text-text mb-sm">
            {t('challenges.times')}
          </label>
          <div className="space-y-sm">
            {value.times?.map((time, index) => (
              <div key={index} className="flex items-center gap-sm">
                <input
                  type="time"
                  value={time}
                  onChange={(e) => handleTimeChange(index, e.target.value)}
                  className="px-md py-sm border border-border rounded-full bg-bg text-text dark-time-input"
                />
                {(value.times?.length || 0) > 1 && (
                  <Button
                    type="button"
                    variant="tertiary"
                    size="sm"
                    onClick={() => removeTime(index)}
                  >
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    {t('common.remove')}
                  </Button>
                )}
              </div>
            ))}
            <Button type="button" variant="secondary" size="sm" onClick={addTime}>
              {t('challenges.addTime')}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
} 